/**
 * DMBadgeManager
 * Manages DM badge in MainLayout sidebar
 *
 * @purpose Update badge count based on unread DMs
 * @used-by MainLayout
 */

import { EventBus } from '../../../services/EventBus';
import { DMService } from '../../../services/dm/DMService';
import { AuthService } from '../../../services/AuthService';

export class DMBadgeManager {
  private eventBus: EventBus;
  private dmService: DMService;
  private authService: AuthService;
  private badgeElement: HTMLElement | null = null;
  private subscriptionIds: string[] = [];

  constructor(badgeElement: HTMLElement) {
    this.badgeElement = badgeElement;
    this.eventBus = EventBus.getInstance();
    this.dmService = DMService.getInstance();
    this.authService = AuthService.getInstance();

    this.setupEventListeners();
    // Don't call updateBadgeCount() here - wait for dm:fetch-complete or dm:badge-update
    // This fixes the race condition where badge tried to update before DMs were loaded
  }

  /**
   * Setup event listeners for badge updates
   */
  private setupEventListeners(): void {
    // Update badge when DM fetch completes (initial load)
    this.subscriptionIds.push(
      this.eventBus.on('dm:fetch-complete', () => {
        this.updateBadgeCount();
      })
    );

    // Update badge on explicit badge-update events (mark read/unread, new messages)
    this.subscriptionIds.push(
      this.eventBus.on('dm:badge-update', () => {
        this.updateBadgeCount();
      })
    );
  }

  /**
   * Update DM badge with unread count
   */
  public async updateBadgeCount(): Promise<void> {
    if (!this.badgeElement) return;

    // Only show badge if user is logged in
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) {
      this.badgeElement.style.display = 'none';
      return;
    }

    try {
      const unreadCount = await this.dmService.getUnreadCount();

      if (unreadCount > 0) {
        this.badgeElement.textContent = unreadCount > 99 ? '99+' : unreadCount.toString();
        this.badgeElement.style.display = 'inline-flex';
      } else {
        this.badgeElement.style.display = 'none';
      }
    } catch {
      // Silently fail - badge is not critical
      this.badgeElement.style.display = 'none';
    }
  }

  /**
   * Cleanup
   */
  public destroy(): void {
    this.subscriptionIds.forEach(id => this.eventBus.off(id));
    this.subscriptionIds = [];
  }
}
